import React, { useEffect, useRef } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

interface DcrPoint {
  lat: number;
  lng: number;
  outletName?: string;
  time?: string;
}

interface OlaMapComponentProps {
  mapCenter: [number, number];
  mapRoutePoints: {
    start: [number, number] | null;
    dcr: [number, number] | null;
    dcrs?: DcrPoint[];
    end: [number, number] | null;
  };
  mapRoute: [number, number][];
  mapEmpInfo: any;
}

const OLA_MAPS_API_KEY = "xQQ3WZCsSELLoYDREVtdCPN8dnn8fl3p7L4j3V2P";

const OlaMapComponent: React.FC<OlaMapComponentProps> = ({ mapCenter, mapRoutePoints, mapRoute, mapEmpInfo }) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);

  useEffect(() => {
    if (!mapContainer.current) return;

    // We must reverse the coordinates for MapLibre since it uses [lng, lat]
    
    const map = new maplibregl.Map({
      container: mapContainer.current,
      style: `https://api.olamaps.io/tiles/vector/v1/styles/default-light-standard/style.json?api_key=${OLA_MAPS_API_KEY}`,
      center: [mapCenter[1], mapCenter[0]], // [lng, lat]
      zoom: 14,
      attributionControl: false, // Disable default to add a custom one if needed, or leave true
      transformRequest: (url: string) => {
        if (url.includes('api.olamaps.io')) {
          // Remove the "key=" parameter if it exists, as Ola Maps returns 404 for key=0.0.1
          let cleanUrl = url.replace(/([?&])key=[^&]*/, '');
          // If we removed the first param (after ?), the next param might start with &, so fix it
          cleanUrl = cleanUrl.replace(/\?&/, '?');
          if (cleanUrl.endsWith('?')) cleanUrl = cleanUrl.slice(0, -1);
          
          if (!cleanUrl.includes('api_key=')) {
            const separator = cleanUrl.includes('?') ? '&' : '?';
            return {
              url: `${cleanUrl}${separator}api_key=${OLA_MAPS_API_KEY}`
            };
          }
          return { url: cleanUrl };
        }
        return { url };
      }
    });
    
    mapRef.current = map;

    map.addControl(new maplibregl.NavigationControl(), 'top-right');
    map.addControl(new maplibregl.AttributionControl({
        customAttribution: 'OLA Maps'
    }));

    map.on('load', () => {
      // Add markers
      const addMarker = (coords: [number, number], color: string, title: string, subtitle: string, label: string) => {
        const el = document.createElement('div');
        el.className = 'custom-marker';
        el.style.backgroundColor = color;
        el.style.width = '26px';
        el.style.height = '26px';
        el.style.borderRadius = '50%';
        el.style.border = '3px solid white';
        el.style.boxShadow = '0 2px 8px rgba(0,0,0,0.5)';
        el.style.display = 'flex';
        el.style.alignItems = 'center';
        el.style.justifyContent = 'center';
        el.style.color = 'white';
        el.style.fontWeight = 'bold';
        el.style.fontSize = '12px';
        el.innerHTML = label;
        
        const popup = new maplibregl.Popup({ offset: 25 }).setHTML(`
          <strong>${title} Location</strong><br/>
          <strong>${subtitle}</strong><br/>
          Lat: ${coords[0].toFixed(5)}, Lng: ${coords[1].toFixed(5)}
        `);
        
        new maplibregl.Marker({ element: el })
          .setLngLat([coords[1], coords[0]]) // [lng, lat]
          .setPopup(popup)
          .addTo(map);
      };
      
      if (mapRoutePoints.start) addMarker(mapRoutePoints.start, '#28a745', 'Start', mapEmpInfo?.fullName || '', 'S');
      
      if (mapRoutePoints.dcrs && mapRoutePoints.dcrs.length > 0) {
        mapRoutePoints.dcrs.forEach((dcr, idx) => {
          addMarker([dcr.lat, dcr.lng], '#fd7e14', `DCR ${idx + 1}`, dcr.outletName || '', `D${idx + 1}`);
        });
      } else if (mapRoutePoints.dcr) {
        addMarker(mapRoutePoints.dcr, '#fd7e14', 'DCR', '(Demo)', 'D');
      }

      if (mapRoutePoints.end) addMarker(mapRoutePoints.end, '#dc3545', 'End', mapEmpInfo?.fullName || '', 'E');
      
      // Add route
      if (mapRoute.length >= 2) {
        const geojsonRoute = {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: mapRoute.map(coord => [coord[1], coord[0]]) // [lng, lat]
          }
        };
        
        map.addSource('route', {
          type: 'geojson',
          data: geojsonRoute as any
        });
        
        map.addLayer({
          id: 'route',
          type: 'line',
          source: 'route',
          layout: {
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': '#000000',
            'line-width': 4,
            'line-opacity': 0.85
          }
        });
      }

      // Auto-fit bounds so all points are visible
      const allCoords: [number, number][] = [];
      if (mapRoutePoints.start) allCoords.push(mapRoutePoints.start);
      if (mapRoutePoints.dcrs && mapRoutePoints.dcrs.length > 0) {
        mapRoutePoints.dcrs.forEach(d => allCoords.push([d.lat, d.lng]));
      } else if (mapRoutePoints.dcr) {
        allCoords.push(mapRoutePoints.dcr);
      }
      if (mapRoutePoints.end) allCoords.push(mapRoutePoints.end);

      if (allCoords.length > 0) {
        const bounds = new maplibregl.LngLatBounds();
        allCoords.forEach(c => bounds.extend([c[1], c[0]]));
        map.fitBounds(bounds, {
          padding: 60,
          maxZoom: 16
        });
      }

      map.resize();
    });
    
    // Resize map after modal animations/transitions are complete
    const resizeTimer1 = setTimeout(() => {
      if (mapRef.current) mapRef.current.resize();
    }, 200);

    const resizeTimer2 = setTimeout(() => {
      if (mapRef.current) mapRef.current.resize();
    }, 600);
    
    return () => {
      clearTimeout(resizeTimer1);
      clearTimeout(resizeTimer2);
      map.remove();
    };
  }, [mapCenter, mapRoutePoints, mapRoute, mapEmpInfo]);
  
  return <div ref={mapContainer} style={{ width: '100%', height: '100%' }} />;
};

export default OlaMapComponent;
